"use client";
// Imports
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { DrawerClose } from "@/components/ui/drawer";
import Link from "next/link";
import {
  Placeholder,
  Img,
  Content,
  EventTimesTable,
  AddToMyShowButton,
  ContentButtonLink,
} from "@/components";
import { resolveLink } from "@/lib/utils";
import { useState } from "react";
import { VIEW_DETAILS_COLORS } from "./viewDetailsButtonColors";

function EventPopupButtons({ buttons }) {
  if (!buttons?.length) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {buttons.map((button, index) => (
        <ContentButtonLink
          key={button?.id ?? index}
          item={button}
          className="w-fit"
        />
      ))}
    </div>
  );
}

export function EventDetails({
  userId,
  data,
  type,
  title,
  categories,
  locations,
  showAddToMyShow,
  image,
  eventTimes,
  mapIsActive,
}) {
  const [showMore, setShowMore] = useState(false);
  const popupButtons = data?.popup_buttons?.filter(
    (button) => button?.button_title && resolveLink(button?.url)
  );
  const hasDescription = Boolean(data?.short_description);
  const hasContent = data?.content?.length > 0;

  return (
    <div
      className="grid h-full max-h-[90dvh] overflow-y-auto grid-cols-1 lg:overflow-hidden lg:grid-cols-2"
      style={{
        // Red gum accents instead of theme saddle orange
        "--button-background": "137 33 2",
        "--button-hover-background": "137 33 2",
        "--button-foreground": "255 255 255",
      }}
    >
      <div className="order-last col-span-1 w-full rounded-l-lg lg:order-first bg-background">
        <div className="flex flex-col justify-between p-4 space-y-6 h-full lg:p-8">
          <div className="pr-2 space-y-5 h-full lg:overflow-y-scroll">
            <div className="flex justify-between space-x-5 w-full">
              <h2 className="level-2">{title}</h2>
              <div className="flex justify-end">
                {showAddToMyShow && (
                  <AddToMyShowButton
                    userId={userId}
                    type={type}
                    slug={data.slug}
                    image={image}
                    title={title}
                  />
                )}
              </div>
            </div>

            {categories?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {categories?.map((category, index) => (
                  <Label dark={false} key={index}>
                    {category?.title}
                  </Label>
                ))}
              </div>
            )}
            {(hasDescription || hasContent || popupButtons?.length > 0) && (
              <div className="hidden overflow-y-auto text-sm lg:block">
                <div className="space-y-3">
                  {hasDescription && <p>{data.short_description}</p>}
                  <EventPopupButtons buttons={popupButtons} />
                  {showMore && hasContent && <Content content={data.content} />}
                </div>

                {hasContent && (
                  <button
                    onClick={() => setShowMore(!showMore)}
                    className="mt-3 font-bold underline cursor-pointer"
                  >
                    {showMore ? "Show less" : "Show more"}
                  </button>
                )}
              </div>
            )}
            {(hasDescription || hasContent || popupButtons?.length > 0) && (
              <div className="block pb-4 h-full text-sm lg:hidden">
                <div className="space-y-3">
                  {hasDescription && <p>{data.short_description}</p>}
                  <EventPopupButtons buttons={popupButtons} />
                  {hasContent && <Content content={data.content} />}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <EventTimesTable
              eventTimes={eventTimes}
              everyDay={data?.every_day}
              collectionHandle={type}
            />
            {locations?.length > 0 && mapIsActive && (
              <div className="">
                <p className="pb-2">Where to find</p>
                <div className="flex overflow-y-hidden justify-between items-center w-full">
                  <div className="flex flex-wrap gap-2 justify-start w-full">
                    {locations.map((location, index) => (
                      <Button
                        size="xs"
                        key={index}
                        variant="outline"
                        customColors={VIEW_DETAILS_COLORS}
                      >
                        <Link href={`/map?pin=${location?.slug}`}>
                          {location?.title}
                        </Link>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="relative col-span-1 w-full h-full">
        <div className="h-[300px] p-4 lg:h-full aspect-auto overflow-hidden relative rounded-t-lg lg:rounded-none">
          <DrawerClose asChild>
            <div className="flex absolute top-2 right-2 z-50 justify-center items-center w-8 h-8 rounded-full cursor-pointer text-background lg:hidden bg-foreground">
              <X />
            </div>
          </DrawerClose>
          {image ? (
            <Img className={""} data={image} sizes="(max-width: 1024px) 100vw, 500px" />
          ) : (
            <Placeholder type="image-placeholder" />
          )}
        </div>
      </div>
    </div>
  );
}
